import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingScreen from '../Common/LoadingScreen';
import { analyticsService } from '../../utils/analyticsService';
import { updatePageSEO, SECTION_SEO } from '../../utils/seo';

// Lazy-loaded page components for optimal bundle splitting and performance
const MobileHubView = lazy(() => import('./MobileHubView'));
const MiteinanderPage = lazy(() => import('./MiteinanderPage'));
const MarktplatzPage = lazy(() => import('./MarktplatzPage'));
const FreundePage = lazy(() => import('./FreundePage'));
const ChatPage = lazy(() => import('./ChatPage'));
const LocationPage = lazy(() => import('./LocationPage'));
const ProfilePage = lazy(() => import('./ProfilePage'));
const SettingsPage = lazy(() => import('./SettingsPage'));
const SecurityPoliciesPage = lazy(() => import('./SecurityPoliciesPage'));
const ForumPage = lazy(() => import('./ForumPage'));
const BlogPage = lazy(() => import('./BlogPage'));
const SearchPage = lazy(() => import('./SearchPage'));
const AdminStatsPage = lazy(() => import('./AdminStatsPage'));

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Extract current section from URL
  const getCurrentSection = () => {
    const path = location.pathname.split('/')[2];
    return path || (isMobile ? 'hub' : 'miteinander');
  };

  const [currentSection, setCurrentSection] = useState(getCurrentSection());

  // Automatically track every section transition for privacy-first analytics and dynamic SEO
  useEffect(() => {
    const section = getCurrentSection();
    setCurrentSection(section);
    analyticsService.trackEvent('navigation', 'view_page', section);
    const seo = SECTION_SEO[section];
    if (seo) {
      updatePageSEO(seo.title, seo.description);
    }
  }, [location.pathname]);

  const handleSectionChange = (section) => {
    setCurrentSection(section);
    navigate(`/dashboard/${section}`);
  };

  return (
    <DashboardLayout 
      currentSection={currentSection} 
      onSectionChange={handleSectionChange}
    >
      <Suspense fallback={
        <LoadingScreen />
      }>
        <Routes>
          <Route 
            path="/" 
            element={isMobile ? <MobileHubView onNavigate={handleSectionChange} /> : <Navigate to="/dashboard/miteinander" replace />} 
          />
          <Route 
            path="/hub" 
            element={isMobile ? <MobileHubView onNavigate={handleSectionChange} /> : <Navigate to="/dashboard/miteinander" replace />} 
          />
          <Route path="/miteinander" element={<MiteinanderPage />} />
          <Route path="/marktplatz" element={<MarktplatzPage />} />
          <Route path="/forum" element={<ForumPage />} />
          <Route path="/wissen" element={<BlogPage />} />
          <Route path="/freunde" element={<FreundePage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/location" element={<LocationPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/security" element={<SecurityPoliciesPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/admin" element={<AdminStatsPage />} />
          <Route 
            path="*" 
            element={<Navigate to={isMobile ? "/dashboard/hub" : "/dashboard/miteinander"} replace />} 
          />
        </Routes>
      </Suspense>
    </DashboardLayout>
  );
};

export default Dashboard;
